import React from 'react';
import Calendar from 'react-calendar';
import { isSameDay, isAfter, isBefore, isValid } from 'date-fns';
import 'react-calendar/dist/Calendar.css';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { debug } from '../utils/debug';

// Types matching react-calendar's expected signatures
type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];
type OnArgs = {
  action: string;
  activeStartDate: Date | null;
  value: Value;
  view: string;
};

interface CruiseDatePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  highlightedRange?: [Date, Date] | null;
  highlightedDate?: Date;
  activeStartDate?: Date;
  disabled?: boolean;
  placeholder?: string;
  label: string;
  error?: string;
  required?: boolean;
}

const CruiseDatePicker: React.FC<CruiseDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  highlightedRange,
  highlightedDate,
  activeStartDate,
  disabled = false,
  placeholder = "Select date",
  label,
  error,
  required = false
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [internalActiveStartDate, setInternalActiveStartDate] = React.useState<Date | undefined>(
    activeStartDate && isValid(activeStartDate) ? activeStartDate : undefined
  );
  const { formatDate, getCalendarType } = useDisplayPreferences();

  // Reset to prop value when calendar opens
  const handleOpen = () => {
    if (activeStartDate && isValid(activeStartDate)) {
      setInternalActiveStartDate(activeStartDate);
    }
    setIsOpen(true);
  };

  // Handle activeStartDate changes from calendar navigation
  // react-calendar passes an OnArgs object with action, activeStartDate, value, and view
  const handleActiveStartDateChange = ({ activeStartDate }: OnArgs) => {
    if (activeStartDate instanceof Date && isValid(activeStartDate)) {
      setInternalActiveStartDate(activeStartDate);
    }
  };

  const formatDisplayValue = (date: Date | null) => {
    if (!date || !isValid(date)) return '';
    return formatDate(date);
  };

  // Debug log for value prop
  debug.log('CruiseDatePicker render:', { 
    label, 
    value, 
    valueType: typeof value, 
    isValidValue: value ? isValid(value) : 'null/undefined',
    formattedValue: value ? formatDisplayValue(value) : 'no value'
  });

  // Debug log for date restrictions
  debug.log('CruiseDatePicker date restrictions:', {
    label,
    minDate,
    maxDate,
    minDateFormatted: minDate ? formatDate(minDate) : 'none',
    maxDateFormatted: maxDate ? formatDate(maxDate) : 'none'
  });

  const tileClassName = ({ date }: { date: Date }) => {
    const classes: string[] = [];

    // Check if this date should be disabled and apply our custom disabled class
    if (tileDisabled({ date })) {
      classes.push('date-picker-disabled-day');
    }

    // Selected date
    if (value && isValid(value) && isSameDay(date, value)) {
      classes.push('date-picker-selected');
    }

    // Highlighted date (e.g., departure date on return calendar)
    if (highlightedDate && isValid(highlightedDate) && isSameDay(date, highlightedDate)) {
      classes.push('date-picker-departure-highlight');
    }

    // Range highlighting (for return date picker)
    if (highlightedRange && highlightedRange[0] && highlightedRange[1] && 
        isValid(highlightedRange[0]) && isValid(highlightedRange[1])) {
      const [start, end] = highlightedRange;
      if ((isAfter(date, start) || isSameDay(date, start)) && 
          (isBefore(date, end) || isSameDay(date, end))) {
        if (!isSameDay(date, start) && !isSameDay(date, end)) {
          classes.push('date-picker-range');
        }
      }
    }

    return classes.join(' ');
  };

  const tileDisabled = ({ date }: { date: Date }) => {
    if (minDate && isValid(minDate) && isBefore(date, minDate)) {
      return true;
    }
    if (maxDate && isValid(maxDate) && isAfter(date, maxDate)) {
      return true;
    }
    return false;
  };

  const handleDateChange = (value: Value) => {
    debug.log('CruiseDatePicker handleDateChange called:', { value, label });
    // react-calendar can pass Date, [Date, Date], or null
    if (value instanceof Date) {
      debug.log('Valid date selected:', value, 'for field:', label);
      onChange(value);
      setIsOpen(false);
    } else if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date) {
      // Handle date range - use the first date
      debug.log('Date range selected, using first date:', value[0], 'for field:', label);
      onChange(value[0]);
      setIsOpen(false);
    } else {
      debug.log('Invalid date selection:', value, 'for field:', label);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && '*'}
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && (isOpen ? setIsOpen(false) : handleOpen())}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md text-left focus:outline-none focus:ring-2 focus:ring-secondary-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}`}
        >
          <span className={value ? 'text-gray-900' : 'text-gray-500'}>
            {value ? formatDisplayValue(value) : placeholder}
          </span>
        </button>

        {isOpen && !disabled && (
          <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
            <Calendar
              onChange={handleDateChange}
              value={value}
              minDate={minDate}
              maxDate={maxDate}
              activeStartDate={internalActiveStartDate && isValid(internalActiveStartDate) ? internalActiveStartDate : undefined}
              onActiveStartDateChange={handleActiveStartDateChange}
              tileClassName={tileClassName}
              tileDisabled={tileDisabled}
              calendarType={getCalendarType()}
              className="border-none shadow-none custom-no-today-highlight"
            />
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CruiseDatePicker;
